import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Movement {
  id: string;
  product: string;
  sku: string;
  type: "sale" | "purchase" | "adjustment" | "transfer" | "return";
  quantity: number;
  previousStock: number;
  newStock: number;
  location: string;
  reference: string;
  createdBy: string;
  createdAt: string;
}

const MOCK_MOVEMENTS: Movement[] = [
  { id: "MOV-001", product: "Premium Headphones", sku: "ELEC-001", type: "sale", quantity: -2, previousStock: 47, newStock: 45, location: "Store Front", reference: "ORD-1234", createdBy: "POS System", createdAt: "2024-01-15 14:30" },
  { id: "MOV-002", product: "Wireless Mouse", sku: "ELEC-002", type: "purchase", quantity: 50, previousStock: 70, newStock: 120, location: "Warehouse A", reference: "PO-5678", createdBy: "John Doe", createdAt: "2024-01-15 12:00" },
  { id: "MOV-003", product: "Cotton T-Shirt", sku: "CLTH-001", type: "adjustment", quantity: -8, previousStock: 93, newStock: 85, location: "Store Front", reference: "ADJ-002", createdBy: "Mike Johnson", createdAt: "2024-01-15 10:45" },
  { id: "MOV-004", product: "USB-C Cable", sku: "ELEC-003", type: "transfer", quantity: -20, previousStock: 28, newStock: 8, location: "Warehouse B", reference: "TRF-005", createdBy: "System", createdAt: "2024-01-14 16:30" },
  { id: "MOV-005", product: "Running Shoes", sku: "CLTH-003", type: "return", quantity: 1, previousStock: 17, newStock: 18, location: "Store Front", reference: "RET-001", createdBy: "POS System", createdAt: "2024-01-14 15:15" },
  { id: "MOV-006", product: "Premium Headphones", sku: "ELEC-001", type: "sale", quantity: -3, previousStock: 50, newStock: 47, location: "Store Front", reference: "ORD-1230", createdBy: "POS System", createdAt: "2024-01-14 11:00" },
  { id: "MOV-007", product: "Denim Jeans", sku: "CLTH-002", type: "purchase", quantity: 20, previousStock: 12, newStock: 32, location: "Warehouse A", reference: "PO-5670", createdBy: "Jane Smith", createdAt: "2024-01-13 09:30" },
];

export default function MovementsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [dateRange, setDateRange] = useState<string>("today");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredMovements = MOCK_MOVEMENTS.filter((item) => {
    const matchesSearch =
      item.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || item.type === filterType;
    return matchesSearch && matchesType;
  });

  const todayMovements = MOCK_MOVEMENTS.filter((m) => m.createdAt.includes("2024-01-15")).length;
  const totalIn = MOCK_MOVEMENTS.filter((m) => m.quantity > 0).reduce((acc, m) => acc + m.quantity, 0);
  const totalOut = Math.abs(MOCK_MOVEMENTS.filter((m) => m.quantity < 0).reduce((acc, m) => acc + m.quantity, 0));
  const salesMovements = MOCK_MOVEMENTS.filter((m) => m.type === "sale").length;

  const getTypeInfo = (type: string) => {
    switch (type) {
      case "sale":
        return { icon: "ri-shopping-bag-line", color: "text-emerald-500 bg-emerald-500/20", label: t("posInventory.sale") };
      case "purchase":
        return { icon: "ri-shopping-cart-2-line", color: "text-blue-500 bg-blue-500/20", label: t("posInventory.purchase") };
      case "adjustment":
        return { icon: "ri-equalizer-line", color: "text-amber-500 bg-amber-500/20", label: t("posInventory.adjustment") };
      case "transfer":
        return { icon: "ri-arrow-left-right-line", color: "text-violet-500 bg-violet-500/20", label: t("posInventory.transfer") };
      case "return":
        return { icon: "ri-arrow-go-back-line", color: "text-orange-500 bg-orange-500/20", label: t("posInventory.return") };
      default:
        return { icon: "ri-question-line", color: "text-gray-500 bg-gray-500/20", label: type };
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posInventory.movementsTitle")}</h1>
          <p className={themeClasses.textSecondary}>{t("posInventory.movementsSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posInventory.export")}
          </button>
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-printer-line"></i>
            {t("posInventory.print")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.todayMovements")}</span>
            <i className="ri-history-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{todayMovements}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.totalIn")}</span>
            <i className="ri-arrow-down-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>+{totalIn}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.totalOut")}</span>
            <i className="ri-arrow-up-circle-line text-red-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-red-500`}>-{totalOut}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.salesMovements")}</span>
            <i className="ri-shopping-bag-line text-violet-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{salesMovements}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posInventory.searchMovements")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              <option value="all">{t("posInventory.allTypes")}</option>
              <option value="sale">{t("posInventory.sale")}</option>
              <option value="purchase">{t("posInventory.purchase")}</option>
              <option value="adjustment">{t("posInventory.adjustment")}</option>
              <option value="transfer">{t("posInventory.transfer")}</option>
              <option value="return">{t("posInventory.return")}</option>
            </select>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              <option value="today">{t("posInventory.today")}</option>
              <option value="week">{t("posInventory.thisWeek")}</option>
              <option value="month">{t("posInventory.thisMonth")}</option>
              <option value="custom">{t("posInventory.custom")}</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.dateTime")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.product")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.type")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.quantity")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.stockBefore")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.stockAfter")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.reference")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovements.map((item) => {
                const typeInfo = getTypeInfo(item.type);
                return (
                  <tr key={item.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                    <td className={`px-4 py-3 ${themeClasses.textSecondary} text-sm`}>{item.createdAt}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>{item.product}</p>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>{item.sku}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${typeInfo.color}`}>
                        <i className={typeInfo.icon}></i>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${item.quantity > 0 ? "text-emerald-500" : "text-red-500"}`}>
                      {item.quantity > 0 ? `+${item.quantity}` : item.quantity}
                    </td>
                    <td className={`px-4 py-3 text-right ${themeClasses.textSecondary}`}>{item.previousStock}</td>
                    <td className={`px-4 py-3 text-right font-medium ${themeClasses.text}`}>{item.newStock}</td>
                    <td className={`px-4 py-3`}>
                      <span className={`font-mono text-sm px-2 py-1 rounded ${themeClasses.cardBg} ${themeClasses.text}`}>
                        {item.reference}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-eye-line"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
